import Qs from "qs";
import { Box, BoxHeading, BoxHeader, useQueryParameters, FieldInline } from "@edgetier/client-components";
import { FunctionComponent, memo, useCallback, useMemo } from "react";
import { ITagGroup } from "@edgetier/watchtower-types";
import { parseUrlArray } from "@edgetier/utilities";
import { SpinnerUntil, SubmitOnChange } from "@edgetier/components";
import { useLocation, useHistory } from "react-router-dom";

import { selectTagGroup, toggleUrlTag } from "./anomaly-metric-tag-groups.utilities";
import { IProps } from "./anomaly-metric-tag-groups.types";
import "./anomaly-metric-tag-groups.scss";
import useTagGroups from "hooks-for/tag-groups/use-tag-groups";
import { faTags } from "@fortawesome/pro-solid-svg-icons";
import AnomalyTagsProportionBars from "../anomaly-tags-proportion-bars";
import SelectTagGroups from "components-for/filters/select-tag-groups";
import { Form, Formik } from "formik";

/**
 * Allow the user to select a tag group, see the tags in there and the percentages of the total interactions they
 * account for and how much each has changed recently (because of the current anomaly).
 * @param props.anomalyId     Filter on the anomaly so that the model's tags are used here also.
 * @param props.endDateTime   Closing date in range to filter.
 * @param props.startDateTime Opening date in range to filter.
 * @returns                   Tag group select component and clickable bar chart.
 */
const AnomalyMetricTagGroups: FunctionComponent<IProps> = ({ anomalyId, endDateTime, startDateTime }) => {
    const { push } = useHistory();
    const { search } = useLocation();
    const { data: tagGroups, isFetching } = useTagGroups();
    const { tagGroupId } = useQueryParameters({
        tagGroupId: String,
    });

    // Select the initial tag group after they're loaded and also each time the URL changes.
    const tagGroup = useMemo(() => selectTagGroup(search, tagGroups), [search, tagGroups]);

    const searchParameters = Qs.parse(search, { ignoreQueryPrefix: true });

    // Currently selected tags.
    const tags = useMemo(() => parseUrlArray(searchParameters.tagId), [searchParameters.tagId]);

    // Update the URL when a user selects a tag group.
    const onChangeTagGroup = useCallback(
        (tagGroupId: string) => {
            // If there are existing tags they have to be cleared because they could belong to a different group.
            const { tagId, ...otherParameters } = searchParameters;

            // Make sure all other parameters are preserved.
            return push({ search: Qs.stringify({ ...otherParameters, tagGroupId }) });
        },
        [push, searchParameters]
    );

    // Update the URL when the user selects a tag.
    const onToggleTag = useCallback(
        (tagId: string) => {
            const searchUrl = toggleUrlTag(tagGroup?.tagGroupId, tags, tagId, search);

            push({ search: searchUrl });
        },
        [push, search, tagGroup?.tagGroupId, tags]
    );

    return (
        <SpinnerUntil<ITagGroup> data={tagGroup} isReady={!isFetching}>
            {(selectedTagGroup) => (
                <Box className="anomaly-metric-tag-groups">
                    <div className="anomaly-metric-tag-groups__heading">
                        <BoxHeader>
                            <BoxHeading>Top Tag Changes</BoxHeading>

                            <div className="anomaly-metric-tag-groups__tag-groups">
                                <Formik<{ tagGroupId: string }>
                                    initialValues={{ tagGroupId: tagGroupId ?? tagGroup?.tagGroupId }}
                                    onSubmit={({ tagGroupId }) => onChangeTagGroup(tagGroupId)}
                                >
                                    <Form className="top-tag-changes-form" name="top-tag-changes-form">
                                        <FieldInline icon={faTags}>
                                            <SelectTagGroups name="tagGroupId" isSingleSelect hasAddItemMenu={false} />
                                        </FieldInline>
                                        <SubmitOnChange delay={0} />
                                    </Form>
                                </Formik>
                            </div>
                        </BoxHeader>
                    </div>

                    <AnomalyTagsProportionBars
                        anomalyId={anomalyId}
                        anomalyEndDateTime={endDateTime}
                        anomalyStartDateTime={startDateTime}
                        onToggle={onToggleTag}
                        selectedTagGroup={selectedTagGroup}
                        selectedItems={tags}
                    />
                </Box>
            )}
        </SpinnerUntil>
    );
};

export default memo(AnomalyMetricTagGroups);
